import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import t from "@/lang/lang";
import {
  useContactsContactKindListQuery,
  useContactsContactKindListServiceContactKindsQuery,
  useContactsContactKindPutServiceContactKindMatchMutation,
  useContactsContactKindShowServiceContactKindMatchQuery,
} from "@/services/backend/contacts/api/contact-kind-api";
import { GSMServiceKind } from "@/services/backend/services/types/kinds";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import { Kind } from "@/services/backend/contacts/types/kind";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { useState } from "react";
import { useDebouncedMutation } from "@/shared/lib/debounce/debounce";
import { ServiceContactKind } from "@/services/backend/contacts/types/service-contact-kind";
import { ServiceContactKindMatch } from "@/services/backend/contacts/types/service-contact-kind-match";

export function VbsServiceContactKindMatchCard() {
  const {
    data: serviceContactKinds,
    isLoading,
    error,
  } = useContactsContactKindListServiceContactKindsQuery({
    serviceKind: GSMServiceKind.VBS,
  });

  if (isLoading) {
    return <Skeleton />;
  }

  if (error) {
    return <RTKQueryErrorAlert error={error} />;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("VBS Kontakte")}</CardTitle>
        <CardDescription>
          {t(
            "VBS Vorgänge benötigen Kontakte des Kunden an mehreren Stellen. Hier kann konfiguriert werden mit welcher Kontaktart diese vorausgefüllt werden sollen. Dies funktioniert nur, wenn die Kontaktart mit MS Dynamics Verknüpft ist und dort ein Kontakt gepflegt ist.",
          )}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <ProcessContactTable
          serviceKind={GSMServiceKind.VBS}
          serviceContactKinds={serviceContactKinds!.items}
        />
      </CardContent>
    </Card>
  );
}

function Skeleton() {
  return (
    <Card className="animate-pulse">
      <CardHeader>
        <CardTitle>{t("VBS Kontakte")}</CardTitle>
        <CardDescription>
          {t(
            "VBS Vorgänge benötigen Kontakte an mehreren Stellen. Hier kann konfiguriert werden mit welcher Kontaktarte diese Vorausgefüllt werden sollen. Dies funktioniert nur, wenn die Kontaktart mit MS Dynamics Verknüpft ist und dort ein Kontakt gepflegt ist.",
          )}
        </CardDescription>
      </CardHeader>
    </Card>
  );
}

function ProcessContactTable(props: {
  serviceKind: GSMServiceKind;
  serviceContactKinds: ServiceContactKind[];
}) {
  const { serviceKind, serviceContactKinds } = props;
  const {
    data: contactKinds,
    isLoading,
    error,
  } = useContactsContactKindListQuery({});

  if (isLoading) {
    return (
      <Table className="animate-pulse">
        <TableHeader>
          <TableRow>
            <TableHead>{t("Kontakt im Vorgang")}</TableHead>
            <TableHead>{t("Kontaktart")}</TableHead>
          </TableRow>
        </TableHeader>
      </Table>
    );
  }

  if (error) {
    return <RTKQueryErrorAlert error={error} />;
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>{t("Kontakt im Vorgang")}</TableHead>
          <TableHead>{t("Kontaktart")}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {serviceContactKinds.map((serviceContactKind) => (
          <TableRow key={serviceContactKind}>
            <TableCell>{t(serviceContactKind)}</TableCell>
            <TableCell>
              <SelectContactKindWrapper
                serviceKind={serviceKind}
                serviceContactKind={serviceContactKind}
                contactKinds={contactKinds!.items}
              />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function SelectContactKindWrapper(props: {
  serviceKind: GSMServiceKind;
  serviceContactKind: ServiceContactKind;
  contactKinds: Kind[];
}) {
  const { serviceKind, serviceContactKind, contactKinds } = props;
  const {
    data: currentSelection,
    isLoading,
    error,
  } = useContactsContactKindShowServiceContactKindMatchQuery({
    serviceKind,
    serviceContactKind,
  });

  if (isLoading) {
    return (
      <Select>
        <SelectTrigger className="w-full">
          <SelectValue placeholder={t("Lade...")} />
        </SelectTrigger>
      </Select>
    );
  }

  if (error) {
    return <RTKQueryErrorAlert error={error} />;
  }

  return (
    <SelectContactKind
      serviceKind={serviceKind}
      serviceContactKind={serviceContactKind}
      contactKinds={contactKinds}
      currentSelection={currentSelection}
    />
  );
}

function SelectContactKind(props: {
  serviceKind: GSMServiceKind;
  serviceContactKind: ServiceContactKind;
  contactKinds: Kind[];
  currentSelection: ServiceContactKindMatch | undefined;
}) {
  const { serviceKind, serviceContactKind, contactKinds, currentSelection } =
    props;
  const [request, setRequest] = useState<ServiceContactKindMatch>({
    serviceKind,
    serviceContactKind,
    contactKindId: currentSelection?.contactKindId ?? "",
  });
  const mutation = useContactsContactKindPutServiceContactKindMatchMutation();
  useDebouncedMutation(
    request,
    setRequest,
    mutation,
    {
      serviceKind,
      serviceContactKind,
      contactKindId: currentSelection?.contactKindId ?? "",
    },
    { delay: 0 },
  );

  return (
    <Select
      value={request.contactKindId}
      onValueChange={(newSelection) =>
        setRequest({ ...request, contactKindId: newSelection })
      }
    >
      <SelectTrigger className="w-full">
        <SelectValue placeholder={t("Kontaktart auswählen")} />
      </SelectTrigger>
      <SelectContent>
        <SelectGroup>
          <SelectLabel>{t("Kontaktarten")}</SelectLabel>
          {contactKinds.map((contactKind) => (
            <SelectItem value={contactKind.id} key={contactKind.id}>
              {contactKind.name}
            </SelectItem>
          ))}
        </SelectGroup>
      </SelectContent>
    </Select>
  );
}
